import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ClipboardList, FileDown, FileSpreadsheet, Phone, Mail, ScanLine } from 'lucide-react';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';
import { DriverManifestHeader } from '@/components/public/DriverManifestHeader';
import { DriverManifestTable, type ManifestRow } from '@/components/public/DriverManifestTable';
import { openPassengerListPrint } from '@/components/all-trips/PassengerListPrint';
import QRScanner, { type QRScanResult, type ScanOutcome } from '@/components/QRScanner';
import { findByRef } from '@/lib/checkinRef';
import { makeLocationColorResolver } from '@/lib/locationColors';

const API = process.env.REACT_APP_BACKEND_URL;

const Brand = ({ company, toggle }: { company: any; toggle: React.ReactNode }) => (
  <div className="relative text-center mb-4" data-testid="pl-brand">
    <div className="absolute right-0 top-0">{toggle}</div>
    {company?.logo && <img src={company.logo} alt={company.name || ''} className="h-10 mx-auto mb-1 object-contain" />}
    {company?.name && <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{company.name}</p>}
    {(company?.phone || company?.email) && (
      <p className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-3">
        {company.phone && <a href={`tel:${company.phone}`} className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{company.phone}</a>}
        {company.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{company.email}</span>}
      </p>
    )}
  </div>
);

const StateCard = ({ company, title, message, toggle }: any) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="pl-state-card">
      <Brand company={company} toggle={toggle} />
      <h1 className="text-lg font-bold text-slate-800 mb-1">{title}</h1>
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  </div>
);

// Sjåførens passasjerliste for ÉN tur (/pl/:token) — offentlig, uten innlogging, sjåførformat, avhuking på nett, PDF/Excel-nedlasting.
export default function PublicDriverManifestPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const { lang, setPublicLanguage } = usePublicLanguage(null);
  const toggle = <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />;

  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prev = meta?.content;
    if (meta) meta.content = 'noindex, nofollow';
    return () => { if (meta && prev !== undefined) meta.content = prev; };
  }, []);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await fetch(`${API}/api/public/driver-manifest/${token}`, { cache: 'no-store' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { setInvalid(true); setData(null); return; }
      setInvalid(false); setData(j); setUpdatedAt(new Date());
    } catch { if (!silent) setInvalid(true); else toast.error(t('driverManifest.kunneIkkeOppdatere', 'Kunne ikke oppdatere akkurat nå')); }
    finally { setLoading(false); setRefreshing(false); }
  }, [token, t]);
  useEffect(() => { load(); }, [load]);

  const toggleRow = async (row: ManifestRow, checked: boolean): Promise<boolean> => {
    const apply = (val: boolean, delta: number) => setData((d: any) => d && ({
      ...d, trip: { ...d.trip, checked_in_count: Math.max(0, (d.trip.checked_in_count || 0) + delta),
        passengers: d.trip.passengers.map((p: ManifestRow) => (p.passenger_id === row.passenger_id ? { ...p, checked_in: val } : p)) },
    }));
    setPending((p) => ({ ...p, [row.passenger_id]: true }));
    apply(checked, checked ? 1 : -1);
    try {
      const res = await fetch(`${API}/api/public/driver-manifest/${token}/check-in`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passenger_id: row.passenger_id, booking_number: row.booking_number, checked_in: checked }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.detail || 'error');
      setData((d: any) => d && ({ ...d, trip: { ...d.trip, checked_in_count: j.trip_checked_in_count ?? d.trip.checked_in_count } }));
      return true;
    } catch (e: any) {
      apply(!checked, checked ? -1 : 1);
      toast.error(e?.message === 'error' ? t('driverManifest.avhukFeilet', 'Kunne ikke lagre avhukingen – prøv igjen') : e?.message);
      if (String(e?.message || '').includes('gyldig') || String(e?.message || '').includes('publisert')) load(true);
      return false;
    } finally { setPending((p) => ({ ...p, [row.passenger_id]: false })); }
  };

  const download = async (format: 'pdf' | 'xlsx') => {
    const trip = data?.trip; if (!trip) return;
    const weeklyPlan = { week_number: trip.week_number, days: [{ date: trip.date, day_name: trip.day_name, formatted_date: trip.formatted_date, trips: [trip] }] };
    await openPassengerListPrint({ tripNumbers: [trip.trip_number], weeklyPlan, lang: lang === 'en' ? 'en' : 'no', format, bookingsByTrip: { [trip.trip_number]: trip.bookings || [] }, locColor: makeLocationColorResolver(data.location_colors, data.location_shortcuts) });
  };

  // QR-skann (gjenbrukt skanner): finn raden på DENNE turen, hopp over allerede avhukede, bruk eksisterende avhuk-kall. Returnerer utfall (lyd/vibrasjon).
  const checkInRow = async (row: ManifestRow | undefined, label: string): Promise<ScanOutcome> => {
    if (!row) { toast.error(t('driverManifest.qrPassasjerIkkeFunnet', { defaultValue: 'Fant ingen passasjer {{label}} på denne turen', label })); return 'not_found'; }
    const name = `${row.first_name} ${row.last_name}`.trim() || `#${row.idx}`;
    if (row.checked_in) { toast.info(t('driverManifest.qrAlleredeAvhuket', { defaultValue: '{{name}} er allerede avhuket', name })); return 'already'; }
    const ok = await toggleRow(row, true);
    if (!ok) return 'error';
    toast.success(t('driverManifest.qrAvhuket', { defaultValue: '{{name}} avhuket via QR', name }));
    return 'ok';
  };
  const onScan = async (r: QRScanResult) => {
    const rows: ManifestRow[] = data?.trip?.passengers || [];
    return checkInRow(rows.find((p) => p.passenger_id === r.id), r.id);
  };
  const onRef = async (ref: string) => {
    const rows: ManifestRow[] = data?.trip?.passengers || [];
    return checkInRow(findByRef(rows.map((p) => ({ ...p, id: p.passenger_id })), ref), `#${ref}`);
  };

  if (loading && !data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (invalid || !data) return <StateCard company={{}} toggle={toggle} title={t('driverManifest.lenkenErIkkeGyldig', 'Lenken er ikke gyldig')} message={t('driverManifest.lenkenUgyldigEllerUtlopt', 'Lenken er ugyldig eller utløpt. Kontakt kontoret for en ny lenke.')} />;
  const company = data.company || {};
  if (data.state === 'revoked' || data.state === 'unpublished') return <StateCard company={company} toggle={toggle} title={t('driverManifest.lenkenErTrukket', 'Lenken er ikke lenger aktiv')} message={t('driverManifest.lenkenTrukketMelding', 'Passasjerlisten er ikke lenger publisert til sjåfør. Kontakt kontoret hvis du trenger listen.')} />;
  if (data.state === 'expired') return <StateCard company={company} toggle={toggle} title={t('driverManifest.lenkenErUtlopt', 'Lenken er utløpt')} message={t('driverManifest.lenkenUtloptMelding', 'Passasjerliste-lenker virker til 7 dager etter turen. Kontakt kontoret hvis du trenger listen.')} />;
  if (data.state === 'not_found' || !data.trip) return <StateCard company={company} toggle={toggle} title={t('driverManifest.turenFinnesIkke', 'Turen finnes ikke lenger')} message={t('driverManifest.turenFinnesIkkeMelding', 'Turen er flyttet eller slettet. Kontakt kontoret.')} />;
  const trip = data.trip;
  const clock = updatedAt ? updatedAt.toLocaleTimeString(lang === 'en' ? 'en-GB' : 'nb-NO', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="min-h-screen bg-slate-50 py-4 px-3 sm:px-6" data-testid="pl-page">
      <div className="max-w-5xl mx-auto">
        <Brand company={company} toggle={toggle} />
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 mb-3 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-800 inline-flex items-center gap-2" data-testid="pl-title"><ClipboardList className="w-5 h-5 text-violet-600" />{t('driverManifest.tittel', 'Passasjerliste')}</h1>
              <p className="text-sm text-slate-500 mt-0.5 font-mono" data-testid="pl-trip-number">{trip.trip_number}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setShowScanner(true)} className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-violet-700" data-testid="pl-scan-qr"><ScanLine className="w-3.5 h-3.5" />{t('driverManifest.skannQr', 'Skann QR')}</button>
              <button type="button" onClick={() => download('pdf')} className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid="pl-download-pdf"><FileDown className="w-3.5 h-3.5" />PDF</button>
              <button type="button" onClick={() => download('xlsx')} className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid="pl-download-xlsx"><FileSpreadsheet className="w-3.5 h-3.5" />Excel</button>
              <button type="button" onClick={() => load(true)} disabled={refreshing} className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid="pl-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />{t('driverManifest.oppdater', 'Oppdater')}
              </button>
            </div>
          </div>
          <DriverManifestHeader trip={trip} lang={lang} locColor={makeLocationColorResolver(data.location_colors, data.location_shortcuts)} />
        </div>
        <DriverManifestTable rows={trip.passengers || []} pending={pending} onToggle={toggleRow} />
        <p className="text-[11px] text-slate-400 text-center mt-4" data-testid="pl-footer">
          {t('driverManifest.fotnote', 'Kryss av passasjerene etter hvert som de går om bord – kontoret ser «Opptalt» i Ukesplanen med en gang.')}{clock ? ` · ${t('driverManifest.sistOppdatert', 'Sist oppdatert')} ${clock}` : ''}
        </p>
      </div>
      {showScanner && <QRScanner expectedTripNumber={trip.trip_number} onScan={onScan} onRef={onRef} onClose={() => setShowScanner(false)} />}
    </div>
  );
}
